import React, { useCallback, useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import useApiEp from "../../hooks/useApiEp";
import { Prism as SyntaxHighlighter } from "react-syntax-highlighter";
import { atomDark } from "react-syntax-highlighter/dist/esm/styles/prism";
import MuPb from "../../widgets/MuPb";
import { Alert, Box, Chip, Container, Stack, TextField } from "@mui/material";
import { getErrorResponse, getHeader, toastError } from "../../utils/util";
import BackBtn from "../../widgets/BackBtn";
import PageTitle from "../../widgets/PageTitle";
import WABAFormPreview from "./WabaFormPreview";
import JsonTextEditor from "./JsonTextEditor";
import { LoadingButton } from "@mui/lab";
import axios from "axios";
import { API_ENDPOINT } from "../../utils/constants";
import { toast } from "react-toastify";
import { GlassButtonLoading } from "../../themes/UiElements";
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Typography,
  Divider,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";

const ImplementationGuide = () => {
  return (
    <Box mt={4}>
      <Typography variant="h6" gutterBottom>
        Implementation Guide
      </Typography>
      <Divider sx={{ mb: 2 }} />

      {/* E2EE */}
      <Accordion>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography>🔒 End-to-End Encryption (E2EE)</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Typography variant="body2" paragraph>
            End-to-End Encryption ensures that sensitive form/flow data is only
            visible to your backend and Meta, never exposed during transit.
          </Typography>
          <Typography variant="body2">
            ✅ **Tips:**
            <ul>
              <li>Use a strong AES-256-GCM encryption key for payload data.</li>
              <li>Encrypt messages before sending them to Meta’s API.</li>
              <li>
                Store private keys securely (e.g., AWS KMS, HashiCorp Vault).
              </li>
              <li>Rotate encryption keys periodically.</li>
              <li>Use HTTPS (TLS 1.2/1.3) for transport security.</li>
            </ul>
          </Typography>
          <Typography variant="body2" sx={{ mt: 1 }}>
            🔧 **Backend Flow (Node.js example)**:
          </Typography>
          <SyntaxHighlighter language="javascript" style={atomDark}>
            {`const crypto = require("crypto");
  
  // Encrypt payload before sending to Meta
  function encryptPayload(data, key) {
    const iv = crypto.randomBytes(16);
    const cipher = crypto.createCipheriv("aes-256-gcm", key, iv);
  
    let encrypted = cipher.update(JSON.stringify(data), "utf8", "base64");
    encrypted += cipher.final("base64");
  
    return {
      iv: iv.toString("base64"),
      content: encrypted,
      tag: cipher.getAuthTag().toString("base64"),
    };
  }`}
          </SyntaxHighlighter>
        </AccordionDetails>
      </Accordion>

      {/* Ping Handler */}
      <Accordion>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography>📡 Responding to Meta Server Ping</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Typography variant="body2" paragraph>
            Meta periodically sends a **health check (ping)** to your endpoint
            URI to verify availability. You must respond with `200 OK` and a
            simple payload.
          </Typography>
          <Typography variant="body2">
            ✅ **Tips:**
            <ul>
              <li>Always respond quickly with minimal overhead.</li>
              <li>Return JSON with `{"status: ok, timestamp: new Date()"}`.</li>
              <li>Ensure authentication if Meta expects signed requests.</li>
              <li>Log pings for monitoring uptime.</li>
            </ul>
          </Typography>
          <Typography variant="body2" sx={{ mt: 1 }}>
            🔧 **Backend Endpoint Example (Express.js)**:
          </Typography>
          <SyntaxHighlighter language="javascript" style={atomDark}>
            {`// Health check endpoint
  app.get("/ping", (req, res) => {
    return res.status(200).json({
      status: "ok",
      service: "Flow API",
      timestamp: new Date().toISOString(),
    });
  });`}
          </SyntaxHighlighter>
        </AccordionDetails>
      </Accordion>
    </Box>
  );
};
const FlowDetails = () => {
  const { id } = useParams();
  const [publishing, setPublishing] = useState(false);
  const [updatingEndpoint, setUpdatingEndpoint] = useState(false);
  const [endpoint, setEndpoint] = useState("");
  const { data, isLoading, isError, error, refetch } = useApiEp(
    `/api/get-flow-asset/${id}`
  );
  useEffect(() => {
    if (data?.detail) {
      setEndpoint(data?.detail?.endpoint_uri ?? "");
    }
  }, [data]);
  const handleUpdateEndpoint = useCallback(async () => {
    // Validate URL
    try {
      new URL(endpoint);
    } catch {
      toast.error("Please enter a valid URL.");
      return;
    }

    try {
      setUpdatingEndpoint(true);
      const response = await axios.post(
        `${API_ENDPOINT}/api/update-flow-metadata/${id}`,
        { endpoint_uri: endpoint },
        getHeader()
      );
      refetch();
      toast.success("Endpoint updated successfully!");
    } catch (error) {
      toastError(error);
    } finally {
      setUpdatingEndpoint(false);
    }
  }, [id, endpoint]);
  if (isLoading) {
    return <MuPb />;
  }
  if (isError) {
    return <Alert severity="error">{getErrorResponse(error)}</Alert>;
  }
  async function publishFlow() {
    try {
      setPublishing(true);
      const response = await axios.post(
        `${API_ENDPOINT}/api/publish-flow/${id}`,
        {},
        getHeader()
      );
      refetch();
      toast.success("Published successfully!");
    } catch (error) {
      toastError(error);
    } finally {
      setPublishing(false);
    }
  }
  return (
    <Container sx={{ mt: 3 }}>
      <BackBtn />
      <Box mt={1} maxWidth={800} mx={"auto"}>
        <PageTitle>Flow Details</PageTitle>
        <Stack spacing={2} direction={"row"} justifyContent={"end"}>
          <Chip
            sx={{ alignSelf: "center" }}
            color={data?.detail?.status === "PUBLISHED" ? "success" : "default"}
            label={data?.detail?.status}
          />
          {data?.detail?.status !== "PUBLISHED" && (
            <LoadingButton
              loading={publishing}
              onClick={publishFlow}
              variant="outlined"
            >
              Publish Flow
            </LoadingButton>
          )}
        </Stack>
        <WABAFormPreview formStructure={data.json} />
        <Box>
          <SyntaxHighlighter
            language="json"
            style={atomDark}
            customStyle={{
              margin: 0,
              borderRadius: 0,
              fontSize: "0.875rem",
            }}
          >
            {JSON.stringify(data.detail, null, 2)}
          </SyntaxHighlighter>
        </Box>
        <Box my={3}>
          <Stack direction={"row"} gap={2}>
            <TextField
              fullWidth
              value={endpoint}
              onChange={(e) => setEndpoint(e.target.value)}
              size="small"
              variant="outlined"
              label="Your endpoint"
              error={!endpoint || endpoint.trim() === ""}
              helperText={!endpoint ? "Endpoint is required" : ""}
            />
            <GlassButtonLoading
              loading={updatingEndpoint}
              onClick={handleUpdateEndpoint}
            >
              Update
            </GlassButtonLoading>
          </Stack>
          <ImplementationGuide />
        </Box>
        <Box display={"flex"} justifyContent={"end"}>
          <Link to={`/home/${id}/flow-json-editor`}>Edit Form JSON</Link>
        </Box>
      </Box>
    </Container>
  );
};

export default FlowDetails;
